/**
 * Employee Number Configuration panel — lives inside the Branding tab
 * of Settings. Iter-46. Owner/admin only. Real-time preview updates as
 * the user tweaks prefix / separator / digits / include-year / start.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import { IdentificationBadge, ArrowsClockwise } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

const SEP_OPTIONS = [
  { value: "-",    label: "Hyphen  (EMP-001)" },
  { value: "/",    label: "Slash  (EMP/001)" },
  { value: "none", label: "None  (EMP001)" }, // "none" ⇔ "" — Shadcn Select can't accept an empty value
];

const DEFAULT_CFG = {
  prefix: "EMP",
  separator: "-",
  digits: 3,
  include_year: false,
  start_number: 1,
  auto_generate: true,
};

function formatPreview(cfg, seq) {
  const year = new Date().getFullYear();
  const pad = String(seq).padStart(Math.max(1, Math.min(8, cfg.digits || 1)), "0");
  const parts = [];
  if (cfg.prefix) parts.push(cfg.prefix);
  if (cfg.include_year) parts.push(String(year));
  parts.push(pad);
  return cfg.separator ? parts.join(cfg.separator) : parts.join("");
}

export default function EmployeeNumberConfigPanel() {
  const { user } = useAuth();
  const canEdit = (user?.roles || []).some(r => r === "owner" || r === "admin");
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nextNumber, setNextNumber] = useState(null);

  const load = async () => {
    try {
      const [{ data: c }, { data: n }] = await Promise.all([
        api.get("/settings/employee-number"),
        api.get("/workforce/employees/next-number").catch(() => ({ data: {} })),
      ]);
      setCfg({ ...DEFAULT_CFG, ...c });
      setNextNumber(n?.employee_number || null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoaded(true); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setCfg(c => ({ ...c, [k]: v }));

  // Live preview — always uses `start_number` so the admin sees a stable
  // shape even before any employee is created. Plus a "next in workspace"
  // hint from the backend.
  const preview = useMemo(() => formatPreview(cfg, cfg.start_number || 1), [cfg]);

  const save = async () => {
    setSaving(true);
    try {
      const clean = {
        prefix: (cfg.prefix || "").trim(),
        separator: cfg.separator,
        digits: Math.max(1, Math.min(8, parseInt(cfg.digits, 10) || 3)),
        include_year: !!cfg.include_year,
        start_number: Math.max(1, parseInt(cfg.start_number, 10) || 1),
        auto_generate: !!cfg.auto_generate,
      };
      const { data } = await api.put("/settings/employee-number", clean);
      setCfg({ ...DEFAULT_CFG, ...data });
      // Refresh the live "next number" hint.
      try {
        const { data: n } = await api.get("/workforce/employees/next-number");
        setNextNumber(n?.employee_number || null);
      } catch { /* noop */ }
      toast.success("Employee number configuration saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Card data-testid="empnum-config-card">
      <CardHeader>
        <CardTitle className="text-base font-display tracking-tight flex items-center gap-2">
          <IdentificationBadge size={16} weight="duotone" />
          Employee Number Configuration
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Sets the format for every employee number in this workspace. Numbers
          are unique per workspace and appear on the Employee Master list,
          payroll runs, payslips and exports.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Prefix</Label>
            <Input
              value={cfg.prefix}
              onChange={e => set("prefix", e.target.value.slice(0, 10).replace(/[^A-Za-z0-9_]/g, ""))}
              placeholder="EMP"
              maxLength={10}
              disabled={!loaded || !canEdit}
              data-testid="empnum-prefix"
            />
            <div className="text-[11px] text-muted-foreground">Letters and digits only, up to 10 characters (e.g. EMP, TC, HR).</div>
          </div>
          <div className="space-y-1.5">
            <Label>Separator</Label>
            <Select
              value={cfg.separator === "" ? "none" : cfg.separator}
              onValueChange={v => set("separator", v === "none" ? "" : v)}
              disabled={!loaded || !canEdit}
            >
              <SelectTrigger data-testid="empnum-separator"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card">
                {SEP_OPTIONS.map(o => (
                  <SelectItem key={o.label} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Digits</Label>
            <Input
              type="number" min={1} max={8}
              value={cfg.digits}
              onChange={e => set("digits", parseInt(e.target.value, 10) || 3)}
              disabled={!loaded || !canEdit}
              data-testid="empnum-digits"
            />
            <div className="text-[11px] text-muted-foreground">e.g. 3 → 001, 4 → 0001. Max 8 digits.</div>
          </div>
          <div className="space-y-1.5">
            <Label>Starting number</Label>
            <Input
              type="number" min={1}
              value={cfg.start_number}
              onChange={e => set("start_number", parseInt(e.target.value, 10) || 1)}
              disabled={!loaded || !canEdit}
              data-testid="empnum-start"
            />
            <div className="text-[11px] text-muted-foreground">Auto-generation begins at this number.</div>
          </div>
        </div>

        {/* Toggles + preview */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="flex items-center gap-3 rounded-md border p-3 cursor-pointer">
            <Switch
              checked={!!cfg.include_year}
              onCheckedChange={v => set("include_year", !!v)}
              disabled={!loaded || !canEdit}
              data-testid="empnum-include-year"
            />
            <div className="text-sm">
              <div className="font-medium">Include current year</div>
              <div className="text-[11px] text-muted-foreground">Inserts the 4-digit year between the prefix and the sequence, e.g. EMP-2026-001.</div>
            </div>
          </label>
          <label className="flex items-center gap-3 rounded-md border p-3 cursor-pointer">
            <Switch
              checked={!!cfg.auto_generate}
              onCheckedChange={v => set("auto_generate", !!v)}
              disabled={!loaded || !canEdit}
              data-testid="empnum-auto-generate"
            />
            <div className="text-sm">
              <div className="font-medium">Auto-generate on new employee</div>
              <div className="text-[11px] text-muted-foreground">
                {cfg.auto_generate
                  ? "The next available number pre-fills the Add Employee form. Editable."
                  : "The Add Employee form requires manual input for the number."}
              </div>
            </div>
          </label>
        </div>

        {/* Live preview */}
        <div className="rounded-md border-2 border-primary/30 bg-primary/5 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="empnum-preview">
          <div>
            <div className="overline mb-1">Preview</div>
            <div className="font-mono text-2xl font-bold tabular-nums text-primary" data-testid="empnum-preview-value">
              {preview}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              Shape of an employee number starting at {cfg.start_number || 1}.
            </div>
          </div>
          {nextNumber && (
            <div className="text-right">
              <div className="overline mb-1 flex items-center justify-end gap-1"><ArrowsClockwise size={11} /> Next in this workspace</div>
              <div className="font-mono text-lg font-semibold tabular-nums" data-testid="empnum-next-value">{nextNumber}</div>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button
            onClick={save}
            disabled={saving || !loaded || !canEdit}
            data-testid="empnum-save"
            size="lg"
          >
            {saving ? "Saving…" : "Save employee number settings"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
